import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, styled, Chip } from '@mui/material';
import { history } from 'src/utils/history';
import EventService from 'src/services/EventService';
import LeadStreamsService from 'src/services/LeadStreamsService';

import { ROUTES_URL } from 'src/constants/routes';

import Plus from 'src/icons/Plus';

import { AccordionMenuContainer } from './AccordionMenu';
import ItemMenu from './ItemMenu';

import type { Stream } from 'src/services/LeadStreamsService/agent';

function hasActiveStreamAccess(stream?: Stream | null) {
    return Boolean(stream?.has_active_access || (stream?.active_access_offer_kind && stream?.access_expires_at));
}

const StreamItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
}));

const StreamIcon = styled(Box)(() => ({
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
    lineHeight: 1,
    flexShrink: 0,
}));

const StreamInfo = styled(Box)(() => ({
    flex: 1,
    minWidth: 0,
}));

const StreamName = styled(Typography)(() => ({
    fontSize: 14,
    lineHeight: 1.4,
}));

const ProgressChip = styled(Chip)<{ isEmpty?: boolean }>(({ theme, isEmpty }) => ({
    height: 20,
    fontSize: 12,
    fontWeight: isEmpty ? 400 : 500,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    borderRadius: '12px',
    '& .MuiChip-label': {
        padding: '0 8px',
    },
}));

const NewStreamButton = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
        '& .new-stream-text': {
            color: '#0044FF',
        },
        '& .plus-icon': {
            '& svg path': {
                fill: '#0044FF',
            },
        },
    },
}));

const NewStreamText = styled(Typography)(() => ({
    color: '#647991',
    fontSize: 14,
    transition: 'color 0.2s ease',
}));

const PlusIcon = styled(Box)(() => ({
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: '#E3ECF3',
    flexShrink: 0,
    transition: 'all 0.2s ease',
    '& svg': {
        width: 16,
        height: 16,
        '& path': {
            fill: '#647991',
            transition: 'fill 0.2s ease',
        },
    },
}));

const LeadStreamsMenu: React.FC = () => {
    const [streams, setStreams] = useState<Stream[]>([]);
    const [loading, setLoading] = useState(true);

    const loadStreams = useCallback(async () => {
        try {
            setLoading(true);
            const data = await LeadStreamsService.getStreams();
            // Фильтруем только активные потоки
            const activeStreams = data.filter((stream) => stream.is_active);
            setStreams(activeStreams);
        } catch (error) {
            console.error('Ошибка загрузки потоков лидов:', error);
            setStreams([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStreams();

        // Подписываемся на события открытия меню для обновления потоков
        const subscriber = EventService.subscribe('OPEN_MENU', () => {
            loadStreams();
        });

        // Обновляем данные при фокусе окна (для десктопа, где меню всегда открыто)
        const handleFocus = () => {
            loadStreams();
        };
        window.addEventListener('focus', handleFocus);

        return () => {
            subscriber.unsubscribe();
            window.removeEventListener('focus', handleFocus);
        };
    }, [loadStreams]);

    const handleNewStreamClick = useCallback(() => {
        history.push(ROUTES_URL.LEADSTREAM);
        EventService.dispatch('CLOSE_MENU');
    }, []);

    const handleStreamClick = useCallback((stream: Stream) => {
        history.push(ROUTES_URL.LEADSTREAM);
        EventService.dispatch('CLOSE_MENU');
    }, []);

    return (
        <Box
            sx={{
                backgroundColor: '#EFF3F7',
                borderRadius: 2,
                p: 1,
            }}
        >
            {streams.length === 0 ? (
                <NewStreamButton onClick={handleNewStreamClick}>
                    <NewStreamText
                        variant="body1"
                        className="new-stream-text"
                    >
                        Новый поток
                    </NewStreamText>
                    <PlusIcon className="plus-icon">
                        <Plus />
                    </PlusIcon>
                </NewStreamButton>
            ) : (
                <>
                    {streams.map((stream) => {
                        // package_size - это сумма всех оплаченных пакетов (status='paid')
                        // Если package_size === 0, но balance > 0, используем balance как общее количество
                        const packageSize = stream.package_size || 0;
                        const balance = stream.balance || 0;
                        const totalSize = hasActiveStreamAccess(stream) ? '∞' : packageSize > 0 ? packageSize : balance;
                        const approvedDeliveries = stream.approved_deliveries || 0;
                        const displayName = stream.name || stream.category.name;
                        const isEmpty = !hasActiveStreamAccess(stream) && totalSize === 0 && approvedDeliveries === 0;

                        return (
                            <StreamItem key={stream.id}>
                                <StreamIcon>{stream.category.icon || '📁'}</StreamIcon>
                                <StreamInfo>
                                    <StreamName
                                        variant="body2"
                                        color="primary"
                                    >
                                        {displayName}
                                    </StreamName>
                                </StreamInfo>
                                <ProgressChip
                                    label={`${approvedDeliveries} из ${totalSize}`}
                                    size="small"
                                    isEmpty={isEmpty}
                                />
                            </StreamItem>
                        );
                    })}
                    <NewStreamButton onClick={handleNewStreamClick}>
                        <NewStreamText
                            variant="body1"
                            className="new-stream-text"
                        >
                            Новый поток
                        </NewStreamText>
                        <PlusIcon className="plus-icon">
                            <Plus />
                        </PlusIcon>
                    </NewStreamButton>
                </>
            )}
        </Box>
    );
};

export default React.memo(LeadStreamsMenu);
